import { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
    Package, ShoppingBag, RefreshCw,
    Search, X, ArrowUpDown, Calendar, AlertCircle,
} from "lucide-react";
import classNames from "classnames";
import BuscadorInput from "../../../Store/BuscadorInput.jsx";
import DropdownOrder from "../../../Store/DropdownOrder.jsx";

const fmt = (n, currency = "EUR") =>
    new Intl.NumberFormat("es-ES", { style: "currency", currency: currency || "EUR" }).format(Number(n || 0));

const STATUS_BADGE = {
    pendiente_pago:  "badge-warning",
    confirmado:      "badge-info",
    en_preparacion:  "badge-info",
    enviado:         "badge-primary",
    entregado:       "badge-success",
    cancelado:       "badge-error",
    incidencia:      "badge-error",
};

const CLASSIFICATION_LABEL = {
    digital_only:  "Digital",
    physical_only: "Físico",
    mixed:         "Mixto",
};

const SORT_OPTIONS = [
    { nombre: "Fecha",  valor: "created_at",    icon: <Calendar    className="w-4 h-4" /> },
    { nombre: "Estado", valor: "status_display", icon: <ArrowUpDown className="w-4 h-4" /> },
];

const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
};

const UserOrders = () => {
    const [orders, setOrders]     = useState([]);
    const [loading, setLoading]   = useState(true);
    const [error, setError]       = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [viewMode, setViewMode] = useState({ sortBy: "created_at", sortDirection: "desc" });

    const debouncedSearch = useDebounce(searchTerm, 300);

    const fetchOrders = useCallback(() => {
        setLoading(true);
        setError(null);
        axios
            .get("/orders/mine/", { withCredentials: true })
            .then((res) => {
                const data = res.data;
                setOrders(data.results || data || []);
            })
            .catch((err) => {
                setError(err.response?.data?.detail || "No se pudieron cargar los pedidos.");
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { fetchOrders(); }, [fetchOrders]);

    const filtered = useMemo(() => {
        let list = [...orders];
        if (debouncedSearch) {
            const q = debouncedSearch.toLowerCase();
            list = list.filter(
                (o) =>
                    o.order_number?.toLowerCase().includes(q) ||
                    (o.status_display || o.status || "").toLowerCase().includes(q)
            );
        }
        list.sort((a, b) => {
            const dir = viewMode.sortDirection === "desc" ? -1 : 1;
            if (viewMode.sortBy === "created_at") {
                return dir * (new Date(a.created_at) - new Date(b.created_at));
            }
            if (viewMode.sortBy === "status_display") {
                return dir * (a.status_display || a.status || "").localeCompare(b.status_display || b.status || "");
            }
            return 0;
        });
        return list;
    }, [orders, debouncedSearch, viewMode]);

    const showEmptyState    = !loading && !error && filtered.length === 0;
    const hasActiveFilters  = debouncedSearch || viewMode.sortBy !== "created_at";
    const centerContent     = loading || showEmptyState || !!error;

    return (
        <div className="min-h-full max-h-full flex flex-col gap-3">

            {/* ── Header de controles ─────────────────────────────────────── */}
            <div className="glassCard p-4 z-40">
                {/* Título + contador + refresh */}
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                        <Package className="w-6 h-6 text-primary" />
                        <div>
                            <h2 className="text-xl font-semibold">Mis pedidos</h2>
                            <p className="text-sm text-base-content/60">
                                {orders.length} {orders.length === 1 ? "pedido" : "pedidos"}
                            </p>
                        </div>
                    </div>
                    <button
                        className="btn btn-sm btn-ghost btn-circle"
                        onClick={fetchOrders}
                        title="Actualizar"
                        disabled={loading}
                    >
                        {loading
                            ? <span className="loading loading-spinner loading-xs" />
                            : <RefreshCw className="w-4 h-4" />
                        }
                    </button>
                </div>

                {/* Búsqueda + orden */}
                <div className="flex flex-col-reverse lg:flex-row gap-2">
                    <div className="flex-1 lg:flex-initial min-w-64 lg:min-w-48 lg:max-w-md">
                        <BuscadorInput
                            searchTerm={searchTerm}
                            setSearchTerm={setSearchTerm}
                            className="w-full"
                            placeholder="Buscar por nº de pedido o estado…"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-2 lg:flex lg:flex-row lg:items-center lg:gap-2">
                        <DropdownOrder
                            viewMode={viewMode}
                            setViewMode={setViewMode}
                            arrayDatosOrdenacion={SORT_OPTIONS}
                            className="w-full"
                        />
                    </div>
                </div>

                {/* Pills de filtros activos */}
                {hasActiveFilters && (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                        {debouncedSearch && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-primary/10 text-primary border border-primary/20">
                                <Search className="w-3 h-3 shrink-0" />
                                <span className="max-w-[160px] truncate">"{debouncedSearch}"</span>
                                <button
                                    onClick={() => setSearchTerm("")}
                                    className="ml-0.5 hover:text-error transition-colors"
                                    aria-label="Quitar filtro de búsqueda"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </span>
                        )}
                        {viewMode.sortBy !== "created_at" && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-base-300 text-base-content/70 border border-base-300">
                                <ArrowUpDown className="w-3 h-3 shrink-0" />
                                <span>{SORT_OPTIONS.find((o) => o.valor === viewMode.sortBy)?.nombre ?? "Orden"}</span>
                                <button
                                    onClick={() => setViewMode((prev) => ({ ...prev, sortBy: "created_at", sortDirection: "desc" }))}
                                    className="ml-0.5 hover:text-error transition-colors"
                                    aria-label="Restablecer orden"
                                >
                                    <X className="w-3 h-3" />
                                </button>
                            </span>
                        )}
                    </div>
                )}
            </div>

            {/* ── Contenido principal ─────────────────────────────────────── */}
            <div className={classNames(
                "h-full flex-1 overflow-y-auto scrollbar_mod glassCard",
                centerContent ? "flex items-center justify-center" : "p-4"
            )}>

                {/* Cargando */}
                {loading && (
                    <div className="text-center py-12">
                        <span className="loading loading-spinner loading-lg text-primary" />
                    </div>
                )}

                {/* Error */}
                {error && !loading && (
                    <div className="text-center py-8">
                        <div className="w-16 h-16 mx-auto mb-4 bg-error/10 rounded-full flex items-center justify-center">
                            <AlertCircle className="w-8 h-8 text-error" />
                        </div>
                        <h3 className="text-lg font-medium text-base-content mb-2">Error al cargar pedidos</h3>
                        <p className="text-base-content/60 mb-4">{error}</p>
                        <button onClick={fetchOrders} className="btn btn-outline">
                            <RefreshCw className="w-4 h-4 mr-2" />
                            Reintentar
                        </button>
                    </div>
                )}

                {/* Estado vacío */}
                {showEmptyState && (
                    <div className="text-center py-12">
                        <div className="w-20 h-20 mx-auto mb-6 bg-base-200 rounded-full flex items-center justify-center">
                            <ShoppingBag className="w-10 h-10 text-base-content/40" />
                        </div>
                        {debouncedSearch ? (
                            <>
                                <h3 className="text-lg font-medium text-base-content mb-2">
                                    No se encontraron pedidos
                                </h3>
                                <p className="text-base-content/60 mb-6">
                                    No hay pedidos que coincidan con "{debouncedSearch}"
                                </p>
                                <button onClick={() => setSearchTerm("")} className="btn btn-outline">
                                    Limpiar búsqueda
                                </button>
                            </>
                        ) : (
                            <>
                                <h3 className="text-lg font-medium text-base-content mb-2">
                                    Aún no tienes pedidos
                                </h3>
                                <p className="text-base-content/60 mb-6">
                                    Explora la tienda o crea tu propio diseño personalizado
                                </p>
                                <div className="flex gap-3 justify-center flex-wrap">
                                    <Link to="/store" className="btn btn-primary">
                                        Ir a la tienda
                                    </Link>
                                    <Link to="/customizers" className="btn btn-outline">
                                        Diseñar ahora
                                    </Link>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Tabla de pedidos */}
                {!loading && !error && filtered.length > 0 && (
                    <div className="overflow-x-auto">
                        <table className="table table-sm">
                            <thead>
                                <tr className="text-xs uppercase">
                                    <th>Nº pedido</th>
                                    <th>Fecha</th>
                                    <th>Tipo</th>
                                    <th>Items</th>
                                    <th>Estado</th>
                                    <th className="text-right">Total</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((o) => (
                                    <tr key={o.id} className="hover">
                                        <td className="font-mono text-sm">{o.order_number}</td>
                                        <td className="text-sm">
                                            {new Date(o.created_at).toLocaleDateString("es-ES")}
                                        </td>
                                        <td className="text-sm">
                                            <span className="badge badge-ghost badge-sm">
                                                {CLASSIFICATION_LABEL[o.classification] || "Físico"}
                                            </span>
                                        </td>
                                        <td className="text-sm">{(o.items || []).length}</td>
                                        <td>
                                            <span className={`badge ${STATUS_BADGE[o.status] || "badge-ghost"}`}>
                                                {o.status_display || o.status}
                                            </span>
                                        </td>
                                        <td className="text-right font-semibold">
                                            {fmt(o.total_price, o.currency)}
                                        </td>
                                        <td>
                                            <Link
                                                to={`/profile/mis-pedidos/${o.order_number}`}
                                                className="link link-primary text-sm"
                                            >
                                                Ver
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserOrders;
